import { motion } from 'framer-motion';
import { Typography, Box, Card, CardContent, Stack, CircularProgress, Paper, Button } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { normalizeUSPData } from 'utils/types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import FeedbackButtons from 'components/FeedbackButtons';

const USPSection = ({ usp, isProUser, handleTrialClick, analysisData, trialAllowed }) => {
  const [hoveredItems, setHoveredItems] = useState({});
  const taskId = useSelector((state) => state.currentTaskReducer);
  // Normalize USP data to handle both old and new formats
  const normalizedUSP = normalizeUSPData(usp);
  const uspResults = normalizedUSP.USP || [];
  const distinctionResults = normalizedUSP.DISTINCTION || [];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <div id="usp">
        <Card
          sx={{
            mt: 4,
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,118,255,0.05) 0%, rgba(103,58,183,0.05) 50%, rgba(103,58,183,0) 100%)',
              zIndex: 0
            }}
          />

          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Stack spacing={3}>
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 0,
                    background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'primary.main'
                  }}
                >
                  Unique selling points & Key Differentiators
                </Typography>
              </Box>

              {!usp && <CircularProgress size={24} />}

              {usp && (
                <Box sx={{ position: 'relative' }}>
                  <Stack spacing={1}>
                    {/* USP Section */}
                    {uspResults.length > 0 && (
                      <>
                        <div id="usp-usp">
                          <Typography variant="h5" color="primary">
                            Unique Selling Points
                          </Typography>
                          <Box
                            onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, usp: true }))}
                            onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, usp: false }))}
                          >
                            <Box
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                position: 'relative',
                                isolation: 'isolate',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  padding: '1px',
                                  borderRadius: 'inherit',
                                  background:
                                    'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                                  WebkitMaskComposite: 'xor',
                                  maskComposite: 'exclude',
                                  zIndex: -1
                                },
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                }
                              }}
                            >
                              <Stack spacing={1}>
                                {uspResults.slice(0, 1).map((item, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1
                                    }}
                                  >
                                    <ChevronRight
                                      sx={{
                                        color: 'primary.main',
                                        fontSize: 20,
                                        mt: 0.5
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      {item}
                                    </Typography>
                                  </Box>
                                ))}

                                {!isProUser && uspResults.length > 1 && (
                                  <Box mt={0} sx={{ position: 'relative', overflow: 'hidden', minHeight: '300px' }}>
                                    <Box
                                      sx={{
                                        filter: 'blur(5px)',
                                        userSelect: 'none',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        padding: 0
                                      }}
                                    >
                                      {uspResults.slice(1).map((item, index) => (
                                        <Box
                                          key={index + 1}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            mb: 2
                                          }}
                                        >
                                          <ChevronRight
                                            sx={{
                                              color: 'primary.main',
                                              fontSize: 20,
                                              mt: 0.5
                                            }}
                                          />
                                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {item}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                    <Paper
                                      elevation={3}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: 2,
                                        backdropFilter: 'blur(5px)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        textAlign: 'center'
                                      }}
                                    >
                                      <motion.div
                                        initial={{ scale: 0.5, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                      >
                                        <Typography variant="h6" gutterBottom>
                                          Upgrade to Pro
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                          Unlock all unique selling points and get full access to our premium features.
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          onClick={handleTrialClick}
                                          component={motion.button}
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                        >
                                          {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                                        </Button>
                                      </motion.div>
                                    </Paper>
                                  </Box>
                                )}

                                {isProUser &&
                                  uspResults.slice(1).map((item, index) => (
                                    <Box
                                      key={index + 1}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1
                                      }}
                                    >
                                      <ChevronRight
                                        sx={{
                                          color: 'primary.main',
                                          fontSize: 20,
                                          mt: 0.5
                                        }}
                                      />
                                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                        {item}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Stack>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <FeedbackButtons
                                blockId="usp-section"
                                blockType="usp"
                                blockTitle="Unique Selling Points"
                                blockContent={JSON.stringify(uspResults)}
                                taskId={taskId}
                                serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                                isVisible={!!hoveredItems['usp']}
                              />
                            </Box>
                          </Box>
                        </div>
                      </>
                    )}

                    {/* Distinction Section */}
                    {distinctionResults.length > 0 && (
                      <>
                        <div id="usp-distinction">
                          <Typography variant="h5" color="primary" sx={{ mt: 2 }}>
                            Key Differentiators
                          </Typography>
                          <Box
                            onMouseEnter={() => setHoveredItems((prev) => ({ ...prev, distinction: true }))}
                            onMouseLeave={() => setHoveredItems((prev) => ({ ...prev, distinction: false }))}
                          >
                            <Box
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                position: 'relative',
                                isolation: 'isolate',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  padding: '1px',
                                  borderRadius: 'inherit',
                                  background:
                                    'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                                  WebkitMaskComposite: 'xor',
                                  maskComposite: 'exclude',
                                  zIndex: -1
                                },
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                                }
                              }}
                            >
                              <Stack spacing={1}>
                                {distinctionResults.slice(0, 1).map((item, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1
                                    }}
                                  >
                                    <ChevronRight
                                      sx={{
                                        color: 'primary.main',
                                        fontSize: 20,
                                        mt: 0.5
                                      }}
                                    />
                                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                      {item}
                                    </Typography>
                                  </Box>
                                ))}

                                {!isProUser && distinctionResults.length > 1 && (
                                  <Box mt={0} sx={{ position: 'relative', overflow: 'hidden', minHeight: '300px' }}>
                                    <Box
                                      sx={{
                                        filter: 'blur(5px)',
                                        userSelect: 'none',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        padding: 0
                                      }}
                                    >
                                      {distinctionResults.slice(1).map((item, index) => (
                                        <Box
                                          key={index + 1}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            mb: 2
                                          }}
                                        >
                                          <ChevronRight
                                            sx={{
                                              color: 'primary.main',
                                              fontSize: 20,
                                              mt: 0.5
                                            }}
                                          />
                                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {item}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                    <Paper
                                      elevation={3}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: 2,
                                        backdropFilter: 'blur(5px)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        textAlign: 'center'
                                      }}
                                    >
                                      <motion.div
                                        initial={{ scale: 0.5, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                      >
                                        <Typography variant="h6" gutterBottom>
                                          Upgrade to Pro
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                          Unlock all key differentiators and get full access to our premium features.
                                        </Typography>
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          onClick={handleTrialClick}
                                          component={motion.button}
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                        >
                                          {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                                        </Button>
                                      </motion.div>
                                    </Paper>
                                  </Box>
                                )}

                                {isProUser &&
                                  distinctionResults.slice(1).map((item, index) => (
                                    <Box
                                      key={index + 1}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1
                                      }}
                                    >
                                      <ChevronRight
                                        sx={{
                                          color: 'primary.main',
                                          fontSize: 20,
                                          mt: 0.5
                                        }}
                                      />
                                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                        {item}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Stack>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <FeedbackButtons
                                blockId="distinction-section"
                                blockType="distinction"
                                blockTitle="Key Differentiators"
                                blockContent={JSON.stringify(distinctionResults)}
                                taskId={taskId}
                                serviceName={analysisData ? analysisData['Service Name'] : 'N/A'}
                                isVisible={!!hoveredItems['distinction']}
                              />
                            </Box>
                          </Box>
                        </div>
                      </>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </div>
    </motion.div>
  );
};

export default USPSection;
